import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { LANGUAGES } from "@/lib/format";
import { Plus, Sparkle, PaperPlaneTilt, EnvelopeOpen, CursorClick, EnvelopeSimple, PaperPlane, CheckCircle, Warning, ShieldCheck, UsersThree, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { RichTextEditor } from "@/components/RichTextEditor";

const textToHtml = (t) => {
  if (!t) return "";
  if (/<(p|div|br|img|a|ul|ol|li|h[1-6]|strong|em|span)\b/i.test(t)) return t;
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return t.split(/\n\n+/).filter((p) => p.trim())
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br/>")}</p>`).join("");
};

const statusStyle = {
  sent: "bg-success/10 text-success border-success/20",
  sending: "bg-amber-50 text-amber-600 border-amber-200",
  scheduled: "bg-blue-50 text-blue-600 border-blue-200",
  draft: "bg-secondary text-muted-foreground border-border",
};

function TestEmailDialog() {
  const { data: templates = [] } = useQuery({ queryKey: ["templates"], queryFn: async () => (await api.get("/templates")).data });
  const { data: status } = useQuery({ queryKey: ["email-status"], queryFn: async () => (await api.get("/email/status")).data });
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [language, setLanguage] = useState("en");

  const send = useMutation({
    mutationFn: async () => (await api.post("/email/test", { to, template_id: templateId, language })).data,
    onSuccess: (d) => {
      if (d.status === "sent") toast.success(`Test email sent to ${to} 📬`);
      else if (d.status === "skipped_demo") toast.info("That looks like a demo address (example.com) — skipped to protect sender reputation. Use a real inbox.");
      else if (d.status === "not_configured") toast.error("Email is not configured.");
      else toast.success(`Status: ${d.status}`);
      if (d.status === "sent") { setOpen(false); setTo(""); }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="test-email-btn" className="gap-1.5"><PaperPlane size={16} weight="fill" /> Send test email</Button></DialogTrigger>
      <DialogContent data-testid="test-email-dialog">
        <DialogHeader><DialogTitle>Send a real test email</DialogTitle></DialogHeader>
        {status && (
          <div className="flex items-center gap-2 text-xs rounded-md border border-border bg-secondary/40 px-3 py-2">
            <CheckCircle size={15} weight="fill" className={status.configured ? "text-success" : "text-muted-foreground"} />
            {status.configured ? <>Connected to <b>{status.provider}</b> · from <b>{status.from_email}</b></> : "Email provider not configured"}
          </div>
        )}
        <div className="space-y-3 mt-1">
          <div>
            <label className="text-[13px] font-medium mb-1.5 block">Recipient email</label>
            <input data-testid="test-email-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@yourhotel.com" className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] font-medium mb-1.5 block">Template</label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="test-email-template"><SelectValue placeholder="Choose template" /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[13px] font-medium mb-1.5 block">Language</label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="test-email-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.native}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="test-email-send" disabled={!to || !templateId || send.isPending} onClick={() => send.mutate()} className="gap-1.5">
            {send.isPending ? "Sending…" : <><PaperPlaneTilt size={15} weight="fill" /> Send test</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AllowlistManager({ emailStatus, live, qc }) {
  const [input, setInput] = useState("");
  const list = emailStatus?.allowlist || [];

  const save = async (emails) => {
    try {
      await api.put("/email/allowlist", { emails });
      qc.invalidateQueries({ queryKey: ["email-status"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update list"); }
  };
  const add = async () => {
    const e = input.trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Enter a valid email");
    if (list.includes(e)) { setInput(""); return; }
    await save([...list, e]); setInput("");
    toast.success(`${e} added to approved recipients`);
  };
  const remove = async (e) => { await save(list.filter((x) => x !== e)); };

  return (
    <div data-testid="allowlist-manager" className="mb-4 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold flex items-center gap-1.5"><UsersThree size={16} className="text-primary" /> Approved recipients (live test list)</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {list.length === 0
              ? (live ? "Empty — LIVE emails go to ALL eligible guests (full rollout)." : "Add a few real inboxes to safely test live sending before full rollout.")
              : (live ? `LIVE sends restricted to these ${list.length} address${list.length > 1 ? "es" : ""} only.` : `${list.length} approved — when you enable live sending, only these get emails.`)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="allowlist-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="you@yourhotel.com" className="h-9 w-56 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          <Button size="sm" variant="outline" data-testid="allowlist-add" onClick={add}><Plus size={14} className="mr-1" /> Add</Button>
        </div>
      </div>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="allowlist-chips">
          {list.map((e) => (
            <span key={e} className="text-xs px-2 py-1 rounded-full bg-secondary border border-border flex items-center gap-1.5">
              {e}
              <button data-testid={`allowlist-remove-${e}`} onClick={() => remove(e)} className="text-muted-foreground hover:text-destructive"><X size={12} weight="bold" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Email() {
  const qc = useQueryClient();
  const { data: campaigns = [] } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => (await api.get("/campaigns")).data,
    refetchInterval: (q) => (q.state.data?.some((c) => c.send_status === "sending") ? 3000 : false),
  });
  const { data: segments = [] } = useQuery({ queryKey: ["segments"], queryFn: async () => (await api.get("/segments")).data });
  const { data: emailStatus } = useQuery({ queryKey: ["email-status"], queryFn: async () => (await api.get("/email/status")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "pre-arrival", subject: "", body: "", segment: "", status: "draft" });
  const [prompt, setPrompt] = useState("");
  const [gen, setGen] = useState(false);

  const live = !!emailStatus?.live_sending;
  const toggleLive = async (val) => {
    if (val && !window.confirm("Enable LIVE sending? Real campaign & automation emails will be delivered to real guest inboxes. Only turn this on when you're ready to email actual guests.")) return;
    try {
      await api.put("/email/settings", { live_sending: val });
      qc.invalidateQueries({ queryKey: ["email-status"] });
      toast[val ? "warning" : "success"](val ? "LIVE sending enabled — campaigns will email real guests" : "Safe mode on — campaigns won't email real guests");
    } catch { toast.error("Could not update sending mode"); }
  };

  const generate = async () => {
    if (!prompt.trim()) return toast.info("Describe the email you want first");
    setGen(true);
    try {
      const { data } = await api.post("/ai/generate-email", { campaign_type: form.type, prompt });
      setForm((f) => ({ ...f, subject: data.subject, body: textToHtml(data.body) }));
      toast.success("Claude drafted your email ✨");
    } catch { toast.error("AI generation failed"); }
    finally { setGen(false); }
  };

  const create = useMutation({
    mutationFn: async (status) => (await api.post("/campaigns", { ...form, status })).data,
    onSuccess: (data, status) => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      if (status === "sent") {
        toast.success("Campaign queued 🎉 — sending in the background");
      } else {
        toast.success("Campaign saved as draft");
      }
      setOpen(false); setForm({ name: "", type: "pre-arrival", subject: "", body: "", segment: "", status: "draft" }); setPrompt("");
    },
  });

  const totalSent = campaigns.reduce((a, c) => a + (c.sent || 0), 0);
  const totalOpens = campaigns.reduce((a, c) => a + (c.opens || 0), 0);
  const totalClicks = campaigns.reduce((a, c) => a + (c.clicks || 0), 0);

  return (
    <Page>
      <div data-testid="email-mode-banner" className={`mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-3 ${live ? "bg-amber-50 border-amber-200" : "bg-success/5 border-success/20"}`}>
        <div className="flex items-center gap-2.5">
          {live
            ? <Warning size={20} weight="fill" className="text-amber-600 shrink-0" />
            : <ShieldCheck size={20} weight="fill" className="text-success shrink-0" />}
          <div>
            <div className="text-sm font-semibold">{live ? "LIVE sending is ON" : "Safe mode"}</div>
            <div className="text-xs text-muted-foreground">
              {live
                ? "Campaigns and automations will deliver real emails to real guest inboxes."
                : "Campaigns & automations will NOT email real guests (recipients are suppressed). Test emails you type still send."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground">Live sending</span>
          <Switch data-testid="live-sending-toggle" checked={live} onCheckedChange={toggleLive} />
        </div>
      </div>

      <AllowlistManager emailStatus={emailStatus} live={live} qc={qc} />

      <PageHeader title="Email Marketing" subtitle="Lifecycle campaigns with AI-assisted content — sent via SMTP2GO.">
        <div className="flex items-center gap-2">
          <TestEmailDialog />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-campaign-btn" className="gap-1.5"><Plus size={16} weight="bold" /> New campaign</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Compose campaign</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 md:col-span-1">
                <label className="text-[13px] font-medium mb-1.5 block">Campaign name</label>
                <input data-testid="campaign-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Summer promo" className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="text-[13px] font-medium mb-1.5 block">Type</label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="campaign-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["pre-arrival", "post-stay", "win-back", "promotion", "newsletter"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-[13px] font-medium mb-1.5 block">Target segment</label>
                <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                  <SelectTrigger data-testid="campaign-segment"><SelectValue placeholder="Choose an audience" /></SelectTrigger>
                  <SelectContent>{segments.map((s) => <SelectItem key={s.id} value={s.name}>{s.name} ({s.guest_count})</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="col-span-2 rounded-md border border-primary/20 bg-primary/5 p-3">
                <label className="text-[13px] font-medium mb-1.5 flex items-center gap-1.5 text-primary"><Sparkle size={15} weight="fill" /> AI content assistant</label>
                <div className="flex gap-2">
                  <input data-testid="ai-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. warm win-back offer, 15% off, family-friendly" className="flex-1 h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                  <Button data-testid="ai-generate-btn" type="button" variant="secondary" disabled={gen} onClick={generate} className="gap-1.5 shrink-0">{gen ? "Drafting…" : <><Sparkle size={15} weight="fill" /> Generate</>}</Button>
                </div>
              </div>

              <div className="col-span-2">
                <label className="text-[13px] font-medium mb-1.5 block">Subject</label>
                <input data-testid="campaign-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div className="col-span-2">
                <label className="text-[13px] font-medium mb-1.5 block">Body</label>
                <div data-testid="campaign-body">
                  <RichTextEditor value={form.body} onChange={(html) => setForm({ ...form, body: html })} />
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" data-testid="save-draft-btn" disabled={!form.name || create.isPending} onClick={() => create.mutate("draft")}>Save draft</Button>
              <Button data-testid="send-campaign-btn" className="gap-1.5" disabled={!form.name || !form.subject || create.isPending} onClick={() => create.mutate("sent")}><PaperPlaneTilt size={15} weight="fill" /> Send now</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </PageHeader>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="rounded-lg border border-border bg-card p-4 flex items-center gap-3"><span className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center"><PaperPlaneTilt size={17} weight="fill" /></span><div><div className="font-heading text-xl font-semibold">{totalSent.toLocaleString()}</div><div className="text-xs text-muted-foreground">Emails sent</div></div></div>
        <div className="rounded-lg border border-border bg-card p-4 flex items-center gap-3"><span className="h-9 w-9 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center"><EnvelopeOpen size={17} weight="fill" /></span><div><div className="font-heading text-xl font-semibold">{totalSent ? Math.round(totalOpens / totalSent * 100) : 0}%</div><div className="text-xs text-muted-foreground">Open rate</div></div></div>
        <div className="rounded-lg border border-border bg-card p-4 flex items-center gap-3"><span className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center"><CursorClick size={17} weight="fill" /></span><div><div className="font-heading text-xl font-semibold">{totalSent ? Math.round(totalClicks / totalSent * 100) : 0}%</div><div className="text-xs text-muted-foreground">Click rate</div></div></div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50 text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
              <th className="font-semibold px-4 py-3">Campaign</th>
              <th className="font-semibold px-4 py-3">Status</th>
              <th className="font-semibold px-4 py-3 hidden md:table-cell">Segment</th>
              <th className="font-semibold px-4 py-3 text-right">Sent</th>
              <th className="font-semibold px-4 py-3 text-right hidden lg:table-cell">Opens</th>
              <th className="font-semibold px-4 py-3 text-right hidden lg:table-cell">Clicks</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c, i) => (
              <tr key={c.id} data-testid={`campaign-row-${i}`} className={`border-b border-border last:border-0 ${i % 2 ? "bg-secondary/20" : ""}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center text-muted-foreground"><EnvelopeSimple size={15} /></span>
                    <div><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground truncate max-w-[240px]">{c.subject}</div></div>
                  </div>
                </td>
                <td className="px-4 py-3">{(() => { const s = c.send_status === "sending" ? "sending" : c.status; return <span className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${statusStyle[s]}`}>{s === "sending" ? "sending…" : s}</span>; })()}</td>
                <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{c.segment || "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums">{c.sent?.toLocaleString() || "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums hidden lg:table-cell">{c.sent ? `${Math.round(c.opens / c.sent * 100)}%` : "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums hidden lg:table-cell">{c.sent ? `${Math.round(c.clicks / c.sent * 100)}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Page>
  );
}
